/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, formatISO, isBefore, isValid, parseISO, startOfToday } from 'date-fns';
import * as R from 'ramda';

// Utils
import { userRoles } from 'utils/currentUserRoles';
import { FILTER_ALL } from 'utils/constants';
import { validateRequiredAutocomplete } from 'utils/helpers/formValidations';
import { DATE_GTE_NOW, INVALID_DATE, REQUIRED } from 'utils/helpers/validationMessages';
import { useGetAffiliateOptions } from 'hooks';

// Components
import { MAutocomplete, MDropdown } from 'common';
import CustomerChained from './CustomerChained';

// function to test validation of transfer date
// created outside of component to keep from garbage collecting on rerenders
const getValidationMessage = R.cond([
  [R.isNil, R.always(REQUIRED)],
  [R.complement(isValid), R.always(INVALID_DATE)],
  [date => isBefore(date, startOfToday()), R.always(DATE_GTE_NOW)],
  [R.T, R.always('')],
]);

const OrderDetails = props => {
  const {
    affiliateOptions,
    data,
    locationOptions,
    initialState,
    onSetLocation,
    onSetProduct,
    onSetRvp,
    products,
    productsOptionsLoading,
    reidVaporPressures,
    setAllowContinue,
    setInitialLoad,
    setValue,
    affiliates,
    setInitialState,
  } = props;

  const {
    dataCompanyId,
    locationId,
    originatorIdName,
    productId,
    recipientIdName,
    referenceId,
    rvp,
    transferDate,
  } = data;
  // when affiliates changes (on mount),
  // if there are affiliates and if the user has not already chosen an Originator (which sets originatorIdName),
  // then set dataCompanyId and OriginatorIdName to the first affiliate on the list.
  // if the user has come back to this page by clicking back, checking for an existing originatorIdName prevents resetting their choices.
  useEffect(() => {
    if (affiliates.length && !originatorIdName) {
      const [{ dataCompanyId: id, customerIdName }] = affiliates;
      setValue('dataCompanyId', id);
      setValue('originatorIdName', customerIdName);
      setInitialState(R.assoc('dataCompanyId', id));
      setInitialState(R.assoc('originatorIdName', customerIdName));
    }
  }, [affiliates]);

  const [pickerDate, setPickerDate] = useState(parseISO(transferDate));

  const [validation, setValidation] = useState({
    locationId: '',
    originatorIdName: '',
    productId: '',
    recipientIdName: '',
    referenceId: '',
    rvp: '',
  });

  const setRecipient = val => setValue('recipientIdName', val);
  const setOriginator = val => {
    const affiliate = affiliates.find(
      ({ customerIdName, dataCompanyId: id }) => `${customerIdName}-${id}` === val
    );
    setValue('originatorIdName', affiliate.customerIdName);
  };
  const setProductId = val => setValue('productId', val);
  const setLocationId = val => setValue('locationId', val);
  const setRvp = val => setValue('rvp', val);
  const setVolume = val => setValue('volume', val);
  const setReferenceId = val => setValue('referenceId', val);
  const setChained = val => setValue('chained', val);
  const setDate = val => setValue('transferDate', val);

  // string that holds validation message OR no value if valid
  const invalid = getValidationMessage(pickerDate);

  const prevIsFutureDate = useRef(data.isFutureDate);

  useEffect(() => {
    if (!invalid) {
      const selectedDate = format(pickerDate, 'yyyy-MM-dd');
      setDate(formatISO(pickerDate));

      // Check for future date
      const parsedDate = parseISO(selectedDate);
      const isFuture = parsedDate > startOfToday();
      setValue('transferDate', formatISO(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX"));
      setValue('isFutureDate', isFuture);
      if (R.isNil(initialState.transferDate)) {
        setInitialState(R.assoc('isFutureDate', isFuture));
        setInitialState(R.assoc('transferDate', formatISO(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX")));
      }

      // If changed from future to today or earlier, reset fulfillment option
      if (prevIsFutureDate.current && !isFuture) {
        setValue('inventorySelection', 'EITHER');
        setValue('reconsignmentControl', 'EITHER');
        setAllowContinue(false);
      }
      prevIsFutureDate.current = isFuture;
    } else {
      setDate('');
      setValue('isFutureDate', false);
    }
  }, [pickerDate]);

  const handleNewAffiliate = val => {
    const { dataCompanyId: id } = affiliates.find(
      ({ customerIdName, dataCompanyId: dataId }) => `${customerIdName}-${dataId}` === val
    );
    setValue('dataCompanyId', id);
  };

  const handleSetOriginator = R.compose(
    R.applySpec([setVolume, setRvp, setLocationId, setProductId, setRecipient]),
    R.always(null),
    value => setValidation(R.assoc('originatorIdName', value)),
    validateRequiredAutocomplete,
    R.tap(handleNewAffiliate),
    R.tap(setOriginator),
    R.prop('value')
  );

  const handleSetRecipient = R.compose(
    setValidation,
    R.assoc('recipientIdName'),
    validateRequiredAutocomplete,
    R.tap(setRecipient),
    R.tap(() => setInitialLoad(true)),
    R.prop('value')
  );

  const handleSetLocation = R.compose(
    R.applySpec([setRvp, setProductId]),
    R.always(null),
    setValidation,
    R.assoc('locationId'),
    validateRequiredAutocomplete,
    R.tap(onSetLocation),
    R.tap(setLocationId),
    R.tap(() => setInitialLoad(true)),
    R.prop('value')
  );

  const handleSetProduct = R.compose(
    R.tap(setRvp),
    R.always(null),
    setValidation,
    R.assoc('productId'),
    validateRequiredAutocomplete,
    R.tap(onSetProduct),
    R.tap(setProductId),
    R.tap(() => setInitialLoad(true)),
    R.prop('value')
  );

  const handleSetRvp = R.compose(R.tap(onSetRvp), R.tap(setRvp), R.prop('value'));

  // catches a productId carried over from a copied order that no longer exists in the current products list for this location
  useEffect(() => {
    if (
      !productsOptionsLoading &&
      productId &&
      !products.some(({ value }) => value === productId)
    ) {
      onSetProduct(null);
      setRvp(null);
      setProductId(null);
      setValidation(R.assoc('productId', REQUIRED));
    }
  }, [products, productsOptionsLoading, productId]);

  const [refernceIdExceedsLimit, setrefernceIdExceedsLimit] = useState(false);

  const handleUpdateReferenceId = e => {
    const value = R.path(['target', 'value'], e);
    if (value.length > 10) {
      setrefernceIdExceedsLimit(true);
    } else {
      setReferenceId(value);
      setrefernceIdExceedsLimit(false);
    }
  };

  const handleUpdateChained = e => {
    const value = R.path(['target', 'value'], e);
    const isChained = R.equals('true', value);
    setChained(isChained);

    if (!isChained) {
      setValue('chainContactIds', []);
    }
  };
  const affiliatesOptions = useGetAffiliateOptions([userRoles.INVENTORY_OWNER]);
  const selectedAffiliate = affiliatesOptions.find(
    ({ customerIdName, dataCompanyId: id }) =>
      customerIdName === originatorIdName && id === dataCompanyId
  );
  return (
    <Box
      sx={{
        '& > div': {
          mb: 2,
          width: '100%',
        },
        p: 3,
        width: '100%',
      }}
    >
      <DatePicker
        disablePast
        id="transfer-date"
        label="Transfer Date"
        onChange={setPickerDate}
        slotProps={{
          textField: {
            error: !!invalid,
            helperText: invalid,
            placeholder: 'mm/dd/yyyy',
          },
        }}
        value={pickerDate}
      />
      {affiliatesOptions.length > 1 && (
        <MDropdown
          disableClearable
          error={!R.isEmpty(validation.originatorIdName)}
          helperText={validation.originatorIdName}
          id="originator-select"
          items={affiliatesOptions}
          label="Originator"
          name="originator"
          onChange={handleSetOriginator}
          openOnFocus
          value={selectedAffiliate ? selectedAffiliate.value : ''}
        />
      )}
      <MAutocomplete
        classNames="css-heap-redact"
        error={!R.isEmpty(validation.recipientIdName)}
        helperText={validation.recipientIdName}
        id="recipient-select"
        label="Recipient"
        loading={false}
        name="recipient"
        onChange={R.flip(handleSetRecipient)}
        openOnFocus
        options={affiliateOptions}
        value={recipientIdName}
      />
      <MAutocomplete
        error={!R.isEmpty(validation.locationId)}
        helperText={validation.locationId}
        id="location-select"
        label="Location"
        loading={false}
        name="location"
        onChange={R.flip(handleSetLocation)}
        openOnFocus
        options={locationOptions}
        value={locationId}
      />
      <MAutocomplete
        classNames="css-heap-redact"
        disabled={!locationId}
        error={!R.isEmpty(validation.productId)}
        helperText={validation.productId}
        id="product-select"
        label="Product"
        loading={false}
        name="product"
        onChange={R.flip(handleSetProduct)}
        openOnFocus
        options={products}
        value={productId}
      />
      {reidVaporPressures && reidVaporPressures.length > 1 && (
        <MAutocomplete
          defaultValue={{
            label: 'Any',
            value: FILTER_ALL,
          }}
          disabled={!productId}
          error={!R.isEmpty(validation.rvp)}
          helperText={validation.rvp}
          id="rvp-select"
          label="RVP"
          loading={false}
          name="rvp"
          onChange={R.flip(handleSetRvp)}
          openOnFocus
          options={reidVaporPressures}
          value={rvp}
        />
      )}
      <TextField
        error={refernceIdExceedsLimit}
        helperText={refernceIdExceedsLimit ? 'Max 10 characters' : ''}
        id="referenceId"
        label="Customer Reference ID"
        onBlur={handleUpdateReferenceId}
        onChange={handleUpdateReferenceId}
        value={referenceId || ''}
        variant="outlined"
      />
      <CustomerChained data={data} handleUpdateChained={handleUpdateChained} />
    </Box>
  );
};

OrderDetails.propTypes = {
  affiliateOptions: arrayOf(
    shape({
      customerIdName: string,
      customerNameLong: string,
      customerNameShort: string,
      dataCompanyId: number,
    })
  ).isRequired,
  affiliates: arrayOf(
    shape({
      customerIdName: string,
      customerNameLong: string,
      customerNameShort: string,
      dataCompanyId: number,
    })
  ).isRequired,
  data: shape({
    isFutureDate: bool,
    locationId: number,
    productId: number,
    recipientIdName: string,
    referenceId: string,
    rvp: number,
    transferDate: string,
    volume: string,
  }).isRequired,
  initialState: shape({}).isRequired,
  locationOptions: arrayOf(
    shape({
      locationId: number,
      locationNameLong: string,
      locationNameShort: string,
    })
  ).isRequired,
  onSetLocation: func.isRequired,
  onSetProduct: func.isRequired,
  onSetRvp: func.isRequired,
  products: arrayOf(
    shape({
      productId: number,
      productNameLong: string,
      productNameShort: string,
    })
  ).isRequired,
  productsOptionsLoading: bool.isRequired,
  reidVaporPressures: arrayOf(shape({ label: string, value: oneOfType([number, string]) }))
    .isRequired,
  setAllowContinue: func.isRequired,
  setInitialLoad: func.isRequired,
  setInitialState: func.isRequired,
  setValue: func.isRequired,
};

export default OrderDetails;
